import { ComponentProps, useCallback, useContext, useEffect } from 'react';
import { useBulkBroadcastActionsPermission } from 'src/components/common/BulkActions/BulkBroadcastActions/useBulkBroadcastActionsPermission';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { Stack } from 'src/core/ui';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { GroupListingsModal } from 'src/modals/GroupListings';
import { ContentId } from 'src/utils/constants/contentId';
import { CommandOptionType, Event } from 'src/WebApiController';

export const LaunchGroupListings = ({
  slideLeft,
  events,
  disabled,
}: ComponentProps<typeof GroupListingsModal> & {
  slideLeft?: boolean;
  events?: Event[];
  disabled?: boolean;
}) => {
  const { hasBroadcastPermission } = useBulkBroadcastActionsPermission();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const { setModal } = useContext(ModalContext);
  const {
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();

  const groupListings = useCallback(() => {
    setModal({
      children: <GroupListingsModal events={events} />,
      clickOutsideToClose: true,
      size: slideLeft ? 'slide-left' : 'slide-in',
    });
  }, [setModal, events, slideLeft]);

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (events?.length) {
        setListItemExpansion(
          true,
          events.map((ev) => ev.virtId)
        );
      }

      groupListings();
    },
    [events, groupListings, setListItemExpansion]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (events?.length) {
      setListItemExpansion(
        true,
        events.map((ev) => ev.virtId)
      );
    }

    groupListings();
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingsGroupListings,
        onSelect: openDialogStableRef,
      },
      disabled: !hasBroadcastPermission,
    });

    return () =>
      unregisterCommand({
        commandId: CommandOptionType.ListingsGroupListings,
        disabled: !hasBroadcastPermission,
      });
  }, [
    registerCommand,
    unregisterCommand,
    groupListings,
    openDialogStableRef,
    hasBroadcastPermission,
  ]);

  if (!hasBroadcastPermission) {
    return null;
  }

  return (
    <PosDropdownItem
      key="GroupListings"
      onClick={onLaunchDialog}
      disabled={disabled}
    >
      <Stack alignItems="center" gap="m" width="full">
        <Content id={ContentId.GroupListings} />
      </Stack>
    </PosDropdownItem>
  );
};
